import { defineStore } from 'pinia';
import type { RouteLocationNormalized } from 'vue-router';

export interface Crumb {
  name: string;
  path: string;
  fullName?: string; // Nom complet non tronqué
}

export const useBreadcrumbsStore = defineStore('breadcrumbs', {
  state: () => ({
    history: [] as Crumb[],
    maxVisibleCrumbs: 5,
    showFullPath: false, // Nouvel état pour afficher le chemin complet
  }),
  
  getters: {
    // Retourne les miettes à afficher, avec "..." si nécessaire
    visibleCrumbs: (state) => {
      const { history } = state;
      
      // Si l'historique est vide ou trop court, le retourner tel quel
      if (history.length <= state.maxVisibleCrumbs || state.showFullPath) {
        return history;
      }
      
      // Sinon, afficher le premier élément (Outils), "..." et les derniers éléments
      const firstCrumb = history[0];
      const lastCrumbs = history.slice(-1 * (state.maxVisibleCrumbs - 2)); // -2 pour tenir compte de "Outils" et "..."
      
      return [
        firstCrumb,
        { name: '...', path: '__show_full_path__' }, // Chemin spécial pour identifier cette action
        ...lastCrumbs
      ];
    }
  },
  
  actions: {
    // Fonction pour nettoyer et tronquer les noms
    cleanAndTruncateName(name: string, maxLength: number = 20): { cleanName: string; fullName: string } {
      if (!name) return { cleanName: '', fullName: '' };
      
      // Nettoyer les caractères problématiques
      let cleanName = name
        .replace(/[[\]{}()]/g, '') // Supprimer les crochets et parenthèses
        .replace(/[<>]/g, '') // Supprimer les chevrons
        .replace(/[|\\]/g, '') // Supprimer les pipes et backslashes
        .replace(/\s+/g, ' ') // Remplacer les espaces multiples par un seul
        .trim(); // Supprimer les espaces en début/fin
      
      const fullName = cleanName; // Garder le nom complet nettoyé
      
      // Tronquer si nécessaire
      if (cleanName.length > maxLength) {
        cleanName = cleanName.substring(0, maxLength - 3) + '...';
      }
      
      return { cleanName, fullName };
    },

    // Bascule l'affichage du chemin complet
    toggleFullPath() {
      this.showFullPath = !this.showFullPath;
    },
    
    // Ajoute une nouvelle miette à l'historique
    addCrumb(route: RouteLocationNormalized) {
      // Extraire le nom et le chemin de la route
      let name = route.name?.toString() || '';
      const path = route.fullPath;
      
      // Vérifier si la route est le dashboard
      if (route.path === '/dashboard') {
        // Réinitialiser l'historique avec seulement "Outils"
        this.history = [{ name: 'Outils', path: '/dashboard' }];
        this.showFullPath = false; // Réinitialiser l'affichage complet
        return;
      }
      
      // Ne jamais ajouter la route "/" directement
      if (route.path === '/') {
        return;
      }

      // Gestion spéciale pour le formulaire
      if (route.path === '/form') {
        const mode = route.query.mode as string || 'create';
        const formName = mode === 'create' ? 'Créer MEMoS' :
                        mode === 'edit' ? 'Modifier MEMoS' :
                        mode === 'useModel' ? 'MEMoS depuis modèle' : 'Formulaire';

        // Si on a déjà un formulaire dans l'historique, on met à jour son nom
        const formIndex = this.history.findIndex(crumb => crumb.path.startsWith('/form'));
        if (formIndex !== -1) {
          const { cleanName, fullName } = this.cleanAndTruncateName(formName);
          this.history[formIndex] = { name: cleanName, path, fullName };
          this.history = this.history.slice(0, formIndex + 1); // Tronquer l'historique après le formulaire
          return;
        }

        // Sinon, on l'ajoute à l'historique
        const { cleanName, fullName } = this.cleanAndTruncateName(formName);
        this.history.push({ name: cleanName, path, fullName });
        return;
      }
      
      // Mapper les noms de routes aux noms plus conviviaux
      if (route.name) {
        const routeNameMap: Record<string, string> = {
          'MemosDetail': 'Détail MEMoS',
          'LocationDetail': 'Détail Lieu',
          'Mes memos': 'Mes MEMoS',
          'Mes lieux': 'Mes Lieux',
          'MyAgents': 'Mes Agents',
          'MyDigitalEntities': 'Mes Jeux de données',
          'MyMaterialEntities': 'Mes Objets',
          'MyProjects': 'Mes Projets'
        };
        
        name = routeNameMap[name] || name;
      }
      
      // Nettoyer et tronquer le nom
      const { cleanName, fullName } = this.cleanAndTruncateName(name);
      
      // Vérifier si cette route existe déjà dans l'historique
      const existingIndex = this.history.findIndex(crumb => crumb.path === path);
      
      if (existingIndex !== -1) {
        // Si la route existe déjà, tronquer l'historique jusqu'à ce point
        this.history = this.history.slice(0, existingIndex + 1);
        this.showFullPath = false; // Réinitialiser l'affichage complet
      } else {
        // Sinon, ajouter la nouvelle miette
        this.history.push({ name: cleanName, path, fullName });
      }
    },
    
    // Met à jour le nom d'une miette existante
    updateCrumbName(path: string, newName: string) {
      const index = this.history.findIndex(crumb => crumb.path === path);
      if (index !== -1) {
        const { cleanName, fullName } = this.cleanAndTruncateName(newName);
        this.history[index].name = cleanName;
        this.history[index].fullName = fullName;
      }
    },
    
        // Ajoute une nouvelle miette à l'historique existant
    updateBreadcrumbs(newCrumbs: Crumb[]) {
      if (newCrumbs.length === 0) return;
      
      // Si le premier élément est "Outils", on commence un nouveau chemin
      if (newCrumbs[0].name === 'Outils') {
        // Nettoyer et tronquer tous les noms
        const cleanedCrumbs = newCrumbs.map(crumb => {
          const { cleanName, fullName } = this.cleanAndTruncateName(crumb.name);
          return {
            ...crumb,
            name: cleanName,
            fullName
          };
        });
        //console.log('🍞 Nouveau chemin depuis Outils:', cleanedCrumbs);
        this.history = [...cleanedCrumbs];
        this.showFullPath = false; // Réinitialiser l'affichage complet
        return;
      }
      
      // Sinon, on ajoute à l'historique existant
      // Vérifier si le dernier élément existe déjà
      const lastCrumb = newCrumbs[newCrumbs.length - 1];
      const existingIndex = this.history.findIndex(crumb => crumb.path === lastCrumb.path);
      
      if (existingIndex !== -1) {
        // Si l'élément existe déjà, tronquer l'historique jusqu'à ce point
        this.history = this.history.slice(0, existingIndex + 1);
        // Mettre à jour le nom si nécessaire
        const isAssetDetail = lastCrumb.path.includes('/agent/') || 
                             lastCrumb.path.includes('/location/') || 
                             lastCrumb.path.includes('/material-entity/') || 
                             lastCrumb.path.includes('/project/') || 
                             lastCrumb.path.includes('/objects/') ||
                             lastCrumb.path.includes('/digitalentity/');
        const maxLength = isAssetDetail ? 40 : 20;
        const { cleanName, fullName } = this.cleanAndTruncateName(lastCrumb.name, maxLength);
        this.history[existingIndex].name = cleanName;
        this.history[existingIndex].fullName = fullName;
        this.showFullPath = false; // Réinitialiser l'affichage complet
        //console.log('🍞 Élément existant mis à jour:', this.history);
      } else {
        // Sinon, ajouter le nouvel élément à l'historique
        // Utiliser une limite plus élevée pour les détails d'assets qui peuvent avoir des noms longs
        const isAssetDetail = lastCrumb.path.includes('/agent/') || 
                             lastCrumb.path.includes('/location/') || 
                             lastCrumb.path.includes('/material-entity/') || 
                             lastCrumb.path.includes('/project/') || 
                             lastCrumb.path.includes('/objects/') ||
                             lastCrumb.path.includes('/digitalentity/');
        const maxLength = isAssetDetail ? 40 : 20;
        const { cleanName, fullName } = this.cleanAndTruncateName(lastCrumb.name, maxLength);
        const newCrumb = { 
          name: cleanName, 
          path: lastCrumb.path,
          fullName
        };
        this.history.push(newCrumb);
        //console.log('🍞 Nouvel élément ajouté:', newCrumb, 'Historique complet:', this.history);
      }
    },
    
    // Réinitialise l'historique
    resetBreadcrumbs() {
      this.history = [{ name: 'Outils', path: '/dashboard' }];
      this.showFullPath = false; // Réinitialiser l'affichage complet
    },
    
    // Initialise les breadcrumbs avec la route actuelle
    initializeBreadcrumbs(route: RouteLocationNormalized) {
      this.resetBreadcrumbs();
      
      // Si la route actuelle n'est pas le dashboard, l'ajouter
      if (route.path !== '/dashboard') {
        this.addCrumb(route);
      }
    }
  }
}); 